import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Container,
  Grid, useTheme, Card, CardContent, CardMedia, 
  Avatar, Divider, AppBar, Toolbar
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TerminalIcon from '@mui/icons-material/Terminal';
import CodeIcon from '@mui/icons-material/Code';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

// Team members data
const teamMembers = [
  {
    id: 1,
    name: 'Marc Herlitz',
    title: 'Business Development',
    expertise: 'Software Engineer',
    imagePath: '/team/marc.jpeg',
    linkedin: 'https://www.linkedin.com/in/marcherlitz/'
  },
  {
    id: 2,
    name: 'Daniel San José',
    title: 'Visionary',
    expertise: 'Next Level Robotics Engineer',
    imagePath: '/team/daniel.jpeg',
    linkedin: 'https://www.linkedin.com/in/daniel-san-jos%C3%A9-pro-0683441a5/'
  },
  {
    id: 3,
    name: 'Marcel Rath',
    title: 'Electronics Wizard',
    expertise: 'Robotics Hardware and Electronics',
    imagePath: '/team/marcel.jpeg',
    linkedin: 'https://www.linkedin.com/in/marcel-rath/'
  },
  {
    id: 4,
    name: 'Upendra Bansal',
    title: 'IT Genius',
    expertise: 'Infrastructure for Learning',
    imagePath: '/team/umpar.jpeg',
    linkedin: 'https://www.linkedin.com/in/upendraba/'
  },
  {
    id: 5,
    name: 'Erik Eremenko',
    title: 'IT Genius',
    expertise: 'Infrastructure for Learning',
    imagePath: '/team/erik.jpeg',
    linkedin: 'https://www.linkedin.com/in/erik-eremenko-a225a625a/'
  },
  {
    id: 6,
    name: 'Onkar Repe',
    title: 'Mechanical Guru',
    expertise: 'Robotics Systems',
    imagePath: '/team/onkar.jpeg',
    linkedin: '#'
  },
  {
    id: 7,
    name: 'Radu Cristian',
    title: 'Electronics Wizard',
    expertise: 'Multi-disciplinary Engineering',
    imagePath: '/team/radu.jpeg',
    linkedin: 'https://www.linkedin.com/in/radu-cristian-584931248/'
  },
  {
    id: 8,
    name: 'Eslam Barhoma',
    title: 'Mechanical Guru',
    expertise: 'Systems Infrastructure',
    imagePath: '/team/esmael.jpeg',
    linkedin: 'https://www.linkedin.com/in/eslam-barhoma-955708102/'
  },
  {
    id: 9,
    name: 'Luca Spachtholz',
    title: 'AI Algorithm Mage',
    expertise: 'Machine Learning',
    imagePath: '/team/holz.jpeg',
    linkedin: 'https://www.linkedin.com/in/luca-spachtholz/'
  },
  {
    id: 10,
    name: 'Niklas Schlüter',
    title: 'Algorithmic Deconstructer',
    expertise: 'Robotics Systems',
    imagePath: '/team/niklas.jpeg',
    linkedin: 'https://www.linkedin.com/in/niklas-schl%C3%BCter-97b800265/'
  }
];

// Partners data
const partners = [
  {
    id: 1,
    name: 'BMW Group',
    description: 'Leading automotive manufacturer and innovation partner',
    imagePath: '/partners/bmw.png'
  },
  {
    id: 2,
    name: 'Bosch',
    description: 'Global supplier of technology and services for industrial applications',
    imagePath: '/partners/bosch.png'
  },
  {
    id: 3,
    name: 'Fraunhofer',
    description: 'Europe\'s largest application-oriented research organization',
    imagePath: '/partners/fraunhofer.png'
  },
  {
    id: 4,
    name: 'Technical University of Munich',
    description: 'One of Europe\'s top universities for engineering and technology research',
    imagePath: '/partners/tum.png'
  },
  {
    id: 5,
    name: 'UnternehmerTUM',
    description: 'Center for innovation and business creation at Technical University of Munich',
    imagePath: '/partners/utum.png'
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const handleDemoClick = () => {
    navigate('/dashboard');
  };
  
  return (
    <Box sx={{ background: '#121212' }}>
      
      {/* AppBar */}
      <AppBar position="sticky" sx={{ background: 'rgba(18, 18, 18, 0.95)', backdropFilter: 'blur(8px)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
        <Toolbar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img 
              src="/favicon.png" 
              alt="UNBOX" 
              style={{ height: '50px', marginRight: '12px' }} 
            />
            UNBOX ROBOTICS
          </Typography>
          
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button 
              color="inherit" 
              sx={{ 
                mx: 1,
                px: 2,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => document.getElementById('solution').scrollIntoView({ behavior: 'smooth' })}
            >
              Solution
            </Button>
            
            <Button 
              color="inherit" 
              sx={{ 
                mx: 1,
                px: 2,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => document.getElementById('algorithm').scrollIntoView({ behavior: 'smooth' })}
            >
              Algorithm
            </Button>
            
            <Button 
              color="inherit" 
              sx={{ 
                mx: 1,
                px: 2,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => document.getElementById('team').scrollIntoView({ behavior: 'smooth' })}
            >
              Team
            </Button>
            
            <Button 
              color="inherit" 
              sx={{ 
                mx: 1,
                px: 2,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => document.getElementById('partners').scrollIntoView({ behavior: 'smooth' })}
            >
              Partners
            </Button>
            
            <Button 
              color="inherit" 
              sx={{ 
                mx: 1,
                px: 2,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            >
              Pricing
            </Button>
            
            <Button
              variant="contained"
              onClick={handleDemoClick}
              sx={{ 
                ml: 2,
                px: 3,
                py: 1,
                borderRadius: '8px',
                fontWeight: 600,
                backgroundColor: theme.palette.bmwAccent.midBlue,
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                '&:hover': {
                  backgroundColor: theme.palette.bmwAccent.darkBlue,
                  boxShadow: '0 6px 15px rgba(33, 150, 243, 0.4)'
                }
              }}
            >
              Demo
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #121212 30%, #1a237e 300%)',
          position: 'relative',
          pt: 10,
          pb: 12,
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative', 
                zIndex: 2,
                animation: 'fadeIn 1s ease-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 800,
                    color: '#FFFFFF',
                    lineHeight: 1.2,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 3,
                    textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                  }}
                >
                  SOLVE THE UNBOXING PROBLEM
                </Typography>
                
                <Typography 
                  variant="h5" 
                  color="text.secondary" 
                  paragraph
                  sx={{ 
                    mb: 4,
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  Finally reach 100% automation in your assembly line with our comprehensive unpackaging software and pre-built AI models that work with virtually any robot
                </Typography>

                <Box sx={{ mb: 6, mt: 5 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleDemoClick}
                    startIcon={<RocketLaunchIcon />}
                    sx={{ 
                      mr: 3, 
                      px: 4, 
                      py: 1.8,
                      borderRadius: '12px',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      backgroundColor: theme.palette.bmwAccent.midBlue,
                      boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.bmwAccent.darkBlue,
                        boxShadow: '0 12px 30px rgba(33, 150, 243, 0.4)',
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    See It In Action
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    component="a"
                    href="https://form.typeform.com/to/mu4JQHV1"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      px: 4, 
                      py: 1.8,
                      borderRadius: '12px',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: '#FFFFFF',
                      borderColor: 'rgba(255,255,255,0.5)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#FFFFFF',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative',
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                  '0%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-20px)' },
                  '100%': { transform: 'translateY(0px)' }
                }
              }}>
                <Paper 
                  elevation={10}
                  sx={{ 
                    p: 0, 
                    borderRadius: '24px',
                    overflow: 'hidden',
                    maxWidth: '100%',
                    aspectRatio: '1/1',
                    background: 'rgba(30, 30, 46, 0.7)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 30px rgba(33, 150, 243, 0.2)',
                    position: 'relative'
                  }}
                >
                  <video 
                    src="/videos/ext.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%', 
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      filter: 'drop-shadow(0 0 30px rgba(33, 150, 243, 0.6))'
                    }}
                  />
                </Paper>
                
                {/* Decorative elements */}
                <Box sx={{ 
                  position: 'absolute', 
                  width: '150px', 
                  height: '150px', 
                  borderRadius: '30px',
                  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0))',
                  top: '-40px',
                  left: '-60px',
                  transform: 'rotate(30deg)',
                  zIndex: -1,
                  backdropFilter: 'blur(10px)'
                }} />
                
                <Box sx={{ 
                  position: 'absolute', 
                  width: '100px', 
                  height: '100px', 
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0))',
                  bottom: '-30px',
                  right: '40px',
                  transform: 'rotate(-20deg)',
                  zIndex: -1,
                  backdropFilter: 'blur(10px)'
                }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
        
        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Our Solution Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          background: 'linear-gradient(to bottom, #121212, #1A1D21)',
          overflow: 'hidden'
        }} 
        id="solution"
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                position: 'relative',
                display: 'inline-block',
                mb: 3
              }}
            >
              Our Solution
              <Box sx={{ 
                position: 'absolute',
                width: '30%',
                height: '4px',
                bgcolor: theme.palette.bmwAccent.midBlue,
                bottom: '-10px',
                left: '35%',
                borderRadius: '4px'
              }} />
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                mb: 2,
                color: 'rgba(255,255,255,0.7)'
              }}
            >
              Solve the automated unpackaging challenge with pre-built models
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                color: 'rgba(255,255,255,0.5)'
              }}
            >
              Our models work in 99% of cases with zero configuration
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Paper 
                elevation={8} 
                sx={{ 
                  height: '100%',
                  display: 'flex', 
                  flexDirection: 'column', 
                  background: 'rgba(30, 30, 30, 0.7)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 15px rgba(33, 150, 243, 0.2)'
                  }
                }}
              >
                <Box sx={{ 
                  bgcolor: 'rgba(33, 150, 243, 0.1)', 
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <PrecisionManufacturingIcon sx={{ fontSize: 60, color: theme.palette.bmwAccent.midBlue }} />
                </Box>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Universal Unpackaging
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Works with virtually any robot and package type
                  </Typography>
                </CardContent>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper 
                elevation={8} 
                sx={{ 
                  height: '100%',
                  display: 'flex', 
                  flexDirection: 'column', 
                  background: 'rgba(30, 30, 30, 0.7)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 15px rgba(244, 67, 54, 0.2)'
                  }
                }}
              >
                <Box sx={{ 
                  bgcolor: 'rgba(244, 67, 54, 0.1)', 
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <CodeIcon sx={{ fontSize: 60, color: theme.palette.bmwAccent.red }} />
                </Box>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Instant Integration
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Deploy pre-built unboxing algorithms without AI expertise
                  </Typography>
                </CardContent>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper 
                elevation={8} 
                sx={{ 
                  height: '100%',
                  display: 'flex', 
                  flexDirection: 'column', 
                  background: 'rgba(30, 30, 30, 0.7)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 15px rgba(33, 150, 243, 0.2)'
                  }
                }}
              >
                <Box sx={{ 
                  bgcolor: 'rgba(33, 150, 243, 0.1)', 
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <SmartToyIcon sx={{ fontSize: 60, color: theme.palette.bmwAccent.lightBlue }} />
                </Box>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Out-of-Box Ready
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    99% effective without any customization needed
                  </Typography>
                </CardContent>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper 
                elevation={8} 
                sx={{ 
                  height: '100%',
                  display: 'flex', 
                  flexDirection: 'column', 
                  background: 'rgba(30, 30, 30, 0.7)',
                  backdropFilter: 'blur(15px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 15px rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <Box sx={{ 
                  bgcolor: 'rgba(25, 118, 210, 0.1)', 
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <HandshakeIcon sx={{ fontSize: 60, color: theme.palette.bmwAccent.darkBlue }} />
                </Box>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={700} sx={{ mb: 2 }}>
                    Enterprise Support
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Tailored unpackaging solutions for specialized needs
                  </Typography>
                </CardContent>
              </Paper>
            </Grid>
          </Grid>

          {/* Video Demo Section */}
          <Box sx={{ mt: { xs: 8, md: 12 }, mb: 4, position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" component="h3" gutterBottom fontWeight={700} textAlign="center" sx={{ mb: 3 }}>
              Simple Fine-Tuning When Needed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', mb: 5, textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
              While our models work out-of-the-box for 99% of unpackaging scenarios, you can easily customize for unique cases
            </Typography>
            
            <Paper 
              elevation={10}
              sx={{ 
                p: 0, 
                borderRadius: '20px',
                overflow: 'hidden',
                maxWidth: '900px',
                mx: 'auto',
                background: '#000',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 5px 20px rgba(33, 150, 243, 0.1)'
              }}
            >
              <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 aspect ratio */ }}>
                <video 
                  controls
                  autoPlay
                  loop
                  muted
                  style={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                >
                  <source src="/videos/recording.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </Box>
            </Paper>
            
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto', 
                mt: 4, 
                textAlign: 'center',
                color: 'rgba(255,255,255,0.5)',
                fontStyle: 'italic'
              }}
            >
              For the rare case where customization is needed, our platform makes it effortless to record, annotate, and train specialized unpackaging models
            </Typography>
          </Box>
        </Container>

        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '15%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Our Algorithm at Work Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          background: 'linear-gradient(to bottom, #1A1D21, #121212)',
          overflow: 'hidden'
        }} 
        id="algorithm"
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                position: 'relative',
                display: 'inline-block',
                mb: 3
              }}
            >
              Our Algorithm at Work
              <Box sx={{ 
                position: 'absolute',
                width: '30%',
                height: '4px',
                bgcolor: theme.palette.bmwAccent.midBlue,
                bottom: '-10px',
                left: '35%',
                borderRadius: '4px'
              }} />
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                mb: 2,
                color: 'rgba(255,255,255,0.7)'
              }}
            >
              Witness our unpackaging AI working on various package types
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                color: 'rgba(255,255,255,0.5)'
              }}
            >
              From small electronics to automotive components, our algorithm handles it all
            </Typography>
          </Box>

          <Grid container spacing={5} justifyContent="center">
            {/* Algorithm Demo */}
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h5" component="h3" gutterBottom fontWeight={700} sx={{ color: theme.palette.bmwAccent.lightBlue }}>
                  Universal Unpackaging Technology
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
                  Our algorithm precisely identifies and handles all types of components - shown here working on small electronics parts, but equally effective with automotive components, industrial parts, and more
                </Typography>
              </Box>
              
              <Paper 
                elevation={10}
                sx={{ 
                  p: 0, 
                  borderRadius: '20px',
                  overflow: 'hidden',
                  maxWidth: '100%',
                  aspectRatio: '1/1',
                  background: 'rgba(30, 30, 46, 0.7)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 30px rgba(33, 150, 243, 0.2)',
                  position: 'relative'
                }}
              >
                  <video 
                    src="/videos/algo1.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%', 
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      filter: 'drop-shadow(0 0 30px rgba(33, 150, 243, 0.6))'
                    }}
                  />
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open('https://form.typeform.com/to/mu4JQHV1', '_blank')}
              startIcon={<RocketLaunchIcon />}
              sx={{ 
                px: 4, 
                py: 1.8,
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '1.1rem',
                backgroundColor: theme.palette.bmwAccent.midBlue,
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: theme.palette.bmwAccent.darkBlue,
                  boxShadow: '0 12px 30px rgba(33, 150, 243, 0.4)',
                  transform: 'translateY(-5px)'
                }
              }}
            >
              See More Examples
            </Button>
          </Box>
        </Container>

        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '20%',
          right: '8%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '15%',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(244, 67, 54, 0.03) 0%, rgba(244, 67, 54, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Team Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 }, 
          backgroundColor: '#1A1D21',
          position: 'relative',
          overflow: 'hidden'
        }} 
        id="team"
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                position: 'relative',
                display: 'inline-block',
                mb: 3
              }}
            >
              Our Team
              <Box sx={{ 
                position: 'absolute',
                width: '30%',
                height: '4px',
                bgcolor: theme.palette.bmwAccent.midBlue,
                bottom: '-10px',
                left: '35%',
                borderRadius: '4px'
              }} />
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: 'rgba(255,255,255,0.7)'
              }}
            >
              Meet the experts behind The Unbox Platform
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {teamMembers.map((member) => (
              <Grid item xs={12} sm={6} md={4} key={member.id}>
                <Paper 
                  elevation={5} 
                  sx={{ 
                    height: '100%',
                    display: 'flex', 
                    flexDirection: 'column', 
                    background: 'rgba(30, 30, 30, 0.7)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 20px rgba(33, 150, 243, 0.15)'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="300"
                      image={member.imagePath}
                      alt={member.name}
                      sx={{ 
                        objectFit: 'cover', 
                        objectPosition: 'center top',
                        bgcolor: '#1e1e2e',
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}
                    />
                    <Box sx={{ 
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '60%',
                      background: 'linear-gradient(to top, rgba(30, 30, 30, 1), rgba(30, 30, 30, 0))',
                      zIndex: 1
                    }} />
                  </Box>
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    textAlign: 'center', 
                    p: 3,
                    position: 'relative',
                    mt: -5,
                    zIndex: 2
                  }}>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: theme.palette.bmwAccent.lightBlue, fontWeight: 600, mb: 1 }}>
                      {member.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                      Expert in {member.expertise}
                    </Typography>
                    
                    {member.id === 3 ? (
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<LinkedInIcon />}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          window.open(member.linkedin, '_blank'); 
                        }}
                        sx={{ 
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          color: theme.palette.bmwAccent.lightBlue,
                          borderColor: 'rgba(33, 150, 243, 0.3)',
                          position: 'relative',
                          zIndex: 10,
                          '&:hover': {
                            borderColor: theme.palette.bmwAccent.lightBlue,
                            backgroundColor: 'rgba(33, 150, 243, 0.08)'
                          }
                        }}
                      >
                        LinkedIn
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<LinkedInIcon />}
                        sx={{ 
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          color: theme.palette.bmwAccent.lightBlue,
                          borderColor: 'rgba(33, 150, 243, 0.3)',
                          position: 'relative',
                          zIndex: 5,
                          '&:hover': {
                            borderColor: theme.palette.bmwAccent.lightBlue,
                            backgroundColor: 'rgba(33, 150, 243, 0.08)'
                          }
                        }}
                      >
                        LinkedIn
                      </Button>
                    )}
                  </CardContent>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '20%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '10%',
          left: '8%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(244, 67, 54, 0.02) 0%, rgba(244, 67, 54, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Partners Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(to bottom, #121212, #0A0A0A)',
        position: 'relative',
        overflow: 'hidden'
      }} id="partners">
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                position: 'relative',
                display: 'inline-block',
                mb: 3
              }}
            >
              Our Partners
              <Box sx={{ 
                position: 'absolute',
                width: '30%',
                height: '4px',
                bgcolor: theme.palette.bmwAccent.midBlue,
                bottom: '-10px',
                left: '35%',
                borderRadius: '4px'
              }} />
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: 'rgba(255,255,255,0.7)'
              }}
            >
              Trusted by industry leaders
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {partners.map((partner) => (
              <Grid item xs={12} sm={6} md={4} key={partner.id}>
                <Paper sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: '#FFFFFF', 
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                  }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    p: 4,
                    height: '140px',
                    backgroundColor: '#FFFFFF',
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <CardMedia 
                      component="img"
                      image={partner.imagePath} 
                      alt={partner.name}
                      sx={{ 
                        width: 150, 
                        height: 90, 
                        objectFit: 'contain',
                        bgcolor: 'transparent'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                    <Typography variant="body1" color="black" fontWeight={500}>
                      {partner.description}
                    </Typography>
                  </CardContent>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '30%',
          left: '5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '15%',
          right: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.02) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Pricing Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 }, 
        backgroundColor: '#1A1D21',
        position: 'relative',
        overflow: 'hidden'
      }} id="pricing">
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                position: 'relative',
                display: 'inline-block',
                mb: 3
              }}
            >
              Pricing Plans
              <Box sx={{ 
                position: 'absolute',
                width: '30%',
                height: '4px',
                bgcolor: theme.palette.bmwAccent.midBlue,
                bottom: '-10px',
                left: '35%',
                borderRadius: '4px'
              }} />
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: 'rgba(255,255,255,0.7)'
              }}
            >
              Choose the right plan for your automation needs
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Starter Plan */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper 
                elevation={10}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: 'rgba(30, 30, 30, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${theme.palette.bmwAccent.midBlue}`,
                  boxShadow: `0 0 30px rgba(33, 150, 243, 0.3)`,
                  borderRadius: '16px',
                  position: 'relative',
                  overflow: 'visible',
                  zIndex: 2,
                  transform: 'scale(1.05)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-8px)',
                    boxShadow: `0 15px 35px rgba(0,0,0,0.4), 0 0 30px rgba(33, 150, 243, 0.35)`
                  }
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: -15, 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    bgcolor: theme.palette.bmwAccent.midBlue,
                    px: 3,
                    py: 0.8,
                    borderRadius: '10px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    RECOMMENDED
                  </Typography>
                </Box>
                
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight={700} textAlign="center">
                    Starter
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', my: 4 }}>
                    <Typography variant="h3" component="p" sx={{ color: theme.palette.bmwAccent.midBlue, fontWeight: 800 }}>
                      €4,999
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      per year
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 3, opacity: 0.2 }} />
                  
                  <Box sx={{ my: 4 }}>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <CodeIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Basic unboxing models
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <SmartToyIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      1 robot integration
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <PrecisionManufacturingIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Standard support
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      component="a"
                      href="https://form.typeform.com/to/mu4JQHV1"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        mt: 2,
                        py: 1.5,
                        borderRadius: '12px',
                        color: theme.palette.bmwAccent.lightBlue,
                        borderColor: theme.palette.bmwAccent.lightBlue,
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: theme.palette.bmwAccent.midBlue,
                          backgroundColor: 'rgba(33, 150, 243, 0.1)'
                        }
                      }}
                    >
                      Get Started
                    </Button>
                  </Box>
                </CardContent>
              </Paper>
            </Grid>
            
            {/* Professional Plan */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper 
                elevation={10}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: 'rgba(30, 30, 40, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${theme.palette.bmwAccent.midBlue}`,
                  boxShadow: `0 0 30px rgba(33, 150, 243, 0.3)`,
                  borderRadius: '16px',
                  position: 'relative',
                  overflow: 'visible',
                  zIndex: 2,
                  transform: 'scale(1.05)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-8px)',
                    boxShadow: `0 15px 35px rgba(0,0,0,0.4), 0 0 30px rgba(33, 150, 243, 0.35)`
                  }
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: -15, 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    bgcolor: theme.palette.bmwAccent.midBlue,
                    px: 3,
                    py: 0.8,
                    borderRadius: '10px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    RECOMMENDED
                  </Typography>
                </Box>
                
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight={700} textAlign="center">
                    Professional
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', my: 4 }}>
                    <Typography variant="h3" component="p" sx={{ color: theme.palette.bmwAccent.midBlue, fontWeight: 800 }}>
                      €9,999
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      per year
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 3, opacity: 0.2 }} />
                  
                  <Box sx={{ my: 4 }}>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <CodeIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Advanced unboxing models
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <SmartToyIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Up to 5 robot integrations
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <PrecisionManufacturingIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Priority support
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <HandshakeIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Custom model training
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      component="a"
                      href="https://form.typeform.com/to/mu4JQHV1"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        mt: 2,
                        py: 1.5,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.bmwAccent.midBlue,
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(33, 150, 243, 0.4)',
                        '&:hover': {
                          backgroundColor: theme.palette.bmwAccent.darkBlue,
                          boxShadow: '0 6px 20px rgba(33, 150, 243, 0.5)'
                        }
                      }}
                    >
                      Get Started
                    </Button>
                  </Box>
                </CardContent>
              </Paper>
            </Grid>
            
            {/* Enterprise Plan */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper 
                elevation={6}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  background: 'rgba(30, 30, 30, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.4), 0 0 20px rgba(33, 150, 243, 0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight={700} textAlign="center">
                    Enterprise
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', my: 4 }}>
                    <Typography variant="h3" component="p" sx={{ color: theme.palette.bmwAccent.lightBlue, fontWeight: 800 }}>
                      Custom
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      contact for pricing
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 3, opacity: 0.2 }} />
                  
                  <Box sx={{ my: 4 }}>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <CodeIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      All premium unboxing models
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <SmartToyIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      Unlimited robot integrations
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <PrecisionManufacturingIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      24/7 dedicated support
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2.5, display: 'flex', alignItems: 'center' }}>
                      <HandshakeIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5, fontSize: 22 }} />
                      On-site implementation & training
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      component="a"
                      href="https://form.typeform.com/to/mu4JQHV1"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        mt: 2,
                        py: 1.5,
                        borderRadius: '12px',
                        color: theme.palette.bmwAccent.lightBlue,
                        borderColor: theme.palette.bmwAccent.lightBlue,
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: theme.palette.bmwAccent.midBlue,
                          backgroundColor: 'rgba(33, 150, 243, 0.1)'
                        }
                      }}
                    >
                      Contact Sales
                    </Button>
                  </Box>
                </CardContent>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
              All plans include access to our unboxing platform and software updates.
              Need a custom solution? Contact our sales team for a tailored plan.
            </Typography>
          </Box>
        </Container>

        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.02) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Investors Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(to bottom, #0A0A0A, #121212)',
        position: 'relative',
        overflow: 'hidden'
      }} id="investors">
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                position: 'relative',
                display: 'inline-block',
                mb: 3
              }}
            >
              Help Us Quit Our Jobs
              <Box sx={{ 
                position: 'absolute',
                width: '30%',
                height: '4px',
                bgcolor: theme.palette.bmwAccent.midBlue,
                bottom: '-10px',
                left: '35%',
                borderRadius: '4px'
              }} />
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                mb: 4,
                color: 'rgba(255,255,255,0.7)'
              }}
            >
              We're ready to go all-in on revolutionizing the robotics industry
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto', 
                mb: 6,
                color: 'rgba(255,255,255,0.6)'
              }}
            >
              Our team is passionate about the UNBOX platform and ready to leave our day jobs to focus on it full-time. 
              With your investment, we can dedicate ourselves to building the future of robot automation solutions.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={10}>
              <Paper 
                elevation={10}
                sx={{ 
                  background: 'rgba(25, 118, 210, 0.15)', 
                  backdropFilter: 'blur(15px)',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.3), 0 0 20px rgba(33, 150, 243, 0.15)',
                  transform: 'translateY(0)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.4), 0 0 30px rgba(33, 150, 243, 0.2)'
                  }
                }}
              >
                <CardContent sx={{ p: 5, textAlign: 'center' }}>
                  <Typography variant="h3" gutterBottom sx={{ color: theme.palette.bmwAccent.lightBlue, fontWeight: 800 }}>
                    200.000€
                  </Typography>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    To Make Us Get Started
                  </Typography>
                  <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>
                    This initial funding will allow our team members to quit their jobs and focus full-time on developing the UNBOX platform, 
                    bringing our revolutionary solution to market faster.
                  </Typography>
                  
                  <Button
                    variant="contained"
                    size="large"
                    component="a"
                    href="https://form.typeform.com/to/mu4JQHV1"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      mt: 4,
                      px: 5, 
                      py: 1.8,
                      borderRadius: '12px',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      backgroundColor: theme.palette.bmwAccent.midBlue,
                      boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.bmwAccent.darkBlue,
                        boxShadow: '0 12px 30px rgba(33, 150, 243, 0.4)',
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    Invest in Our Future
                  </Button>
                </CardContent>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute',
          top: '20%',
          left: '8%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0) 70%)',
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute',
          bottom: '15%',
          right: '5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(244, 67, 54, 0.02) 0%, rgba(244, 67, 54, 0) 70%)',
          zIndex: 0
        }} />
      </Box>

      {/* Footer */}
      <Box sx={{ 
        py: 5, 
        backgroundColor: '#0A0A0A', 
        color: 'white',
        borderTop: '1px solid rgba(255,255,255,0.05)'
      }}>
        <Container>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src="/favicon.png" 
                  alt="UNBOX" 
                  style={{ height: '40px', marginRight: '12px' }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  UNBOX ROBOTICS
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.6)' }}>
                © 2025 All Rights Reserved
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button 
                color="inherit" 
                sx={{ 
                  mx: 1,
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: 'rgba(255,255,255,1)' }
                }}
              >
                Privacy
              </Button>
              <Button 
                color="inherit" 
                sx={{ 
                  mx: 1,
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: 'rgba(255,255,255,1)' }
                }}
              >
                Terms
              </Button>
              <Button 
                color="inherit" 
                sx={{ 
                  mx: 1,
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: 'rgba(255,255,255,1)' }
                }}
              >
                Contact
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;